import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectSettingColors } from "../../../../reducers/editor/selectors";
import {
  selectCanvasWidth,
  selectPage,
} from "../../../../reducers/pdf-viewer/selectors";
import {
  selectIsShown,
  selectPdfDetectedFields,
} from "../../../../reducers/pdf-detected-fields/selectors";
import {
  generateRectangleFromDetectedField,
  generateRectangleFromProjectObject,
} from "../../../../helpers/objectGenerators";
import { setDraftingObject } from "../../../../reducers/project/actions";
import {
  selectDraftingObject,
  selectProjectObject,
} from "../../../../reducers/project/selectors";
import { OBJECT_TYPES } from "../../../../constants/project";
import NewRectangle from "../../../new-rectangle";
import { isNil, propOr } from "ramda";
import { Label, Tag, Text } from "react-konva";

const NewItemsBoard = ({
  cursorIsOnCanvasItem,
  removeIsOnCanvasItem,
  setIsTransforming,
  handleOnGroupItemClick,
}) => {
  const dispatch = useDispatch();
  const page = useSelector(selectPage);
  const detectedFields = useSelector(selectPdfDetectedFields);
  const isDetectedFieldsShown = useSelector(selectIsShown);
  const draftingObject = useSelector(selectDraftingObject);
  const projectObjects = useSelector(selectProjectObject);
  const settingColors = useSelector(selectSettingColors);
  const canvasWidth = useSelector(selectCanvasWidth);
  const [hoveringRectObject, setHoveringRectObject] = useState(null);

  const tooltipData = useMemo(() => {
    if (isNil(hoveringRectObject)) {
      return {};
    }

    if (hoveringRectObject.y < 40) {
      return {
        x: hoveringRectObject.x + hoveringRectObject.width / 2,
        y: hoveringRectObject.height
          ? hoveringRectObject.y + hoveringRectObject.height
          : hoveringRectObject.y + 10,
        pointerDirection: "up",
      };
    }

    // if (
    //   hoveringRectObject.width < 140 &&
    //   canvasWidth - (hoveringRectObject.x + hoveringRectObject.width / 2) < 80
    // ) {
    //   return {
    //     x: hoveringRectObject.x,
    //     y: hoveringRectObject.y + hoveringRectObject.height / 2,
    //     pointerDirection: "right",
    //   };
    // }

    // if (
    //   hoveringRectObject.width < 140 &&
    //   hoveringRectObject.x + hoveringRectObject.width / 2 < 80
    // ) {
    //   return {
    //     x: hoveringRectObject.x + hoveringRectObject.width,
    //     y: hoveringRectObject.y + hoveringRectObject.height / 2,
    //     pointerDirection: "left",
    //   };
    // }

    return {
      x: hoveringRectObject.x + hoveringRectObject.width / 2,
      y: hoveringRectObject.y,
      pointerDirection: "down",
    };
  }, [hoveringRectObject]);

  const detectedFieldObjects = useMemo(() => {
    return detectedFields.map((field) =>
      generateRectangleFromDetectedField(field)
    );
  }, [detectedFields, page]);

  const projectObjectRectangles = useMemo(() => {
    return projectObjects.map((projectObject) => {
      if (projectObject.objectType === OBJECT_TYPES.DETECTED_FIELD) {
        const objectItem = detectedFieldObjects.find(
          (item) => item.data.altFieldName === projectObject.altFieldName
        );

        if (objectItem) {
          return { ...objectItem, data: projectObject };
        }
      }
      return generateRectangleFromProjectObject(projectObject);
    });
  }, [projectObjects, page, detectedFieldObjects]);

  const handleOnDoubleClickDetectedFieldRectangle = (e, field) => {
    dispatch(setDraftingObject(field));
  };

  const drawDetectedFieldElements = useCallback(
    (objects) => {
      var elements = [];
      if (objects && objects.length) {
        objects.forEach((object) => {
          const isVisibleElement =
            object.fieldProperties.pageIndex === page - 1;

          if (isVisibleElement) {
            elements.push(
              <NewRectangle
                handleOnGroupItemClick={handleOnGroupItemClick}
                cursorIsOnCanvasItem={cursorIsOnCanvasItem}
                removeIsOnCanvasItem={removeIsOnCanvasItem}
                setHoveringRectObject={setHoveringRectObject}
                canDraggable={false}
                color={settingColors.detectedField}
                key={`field-${object.data.id}`}
                object={object}
                handleOnDoubleClick={handleOnDoubleClickDetectedFieldRectangle}
              />
            );
          }
        });
      }
      return elements;
    },
    [settingColors.detectedField, page]
  );

  const drawProjectObjectElements = useMemo(() => {
    var elements = [];

    if (projectObjectRectangles && projectObjectRectangles.length) {
      projectObjectRectangles.forEach((projectObject) => {
        const isVisibleElement =
          Number(projectObject.fieldProperties.pageIndex) === page - 1 &&
          projectObject.objectId !== propOr(null, "objectId", draftingObject);

        if (isVisibleElement) {
          elements.push(
            <NewRectangle
              handleOnGroupItemClick={handleOnGroupItemClick}
              setIsTransforming={setIsTransforming}
              cursorIsOnCanvasItem={cursorIsOnCanvasItem}
              removeIsOnCanvasItem={removeIsOnCanvasItem}
              setHoveringRectObject={setHoveringRectObject}
              canDraggable={true}
              color={settingColors.currentObject}
              key={`project-${projectObject.data.objectId}`}
              object={projectObject}
              handleOnDoubleClick={handleOnDoubleClickDetectedFieldRectangle}
            />
          );
        }
      });
    }
    return elements;
  }, [
    projectObjectRectangles,
    page,
    settingColors.currentObject,
    draftingObject,
  ]);

  const drawRectangleDraftingElements = useMemo(() => {
    if (!draftingObject) return;
    return (
      <NewRectangle
        handleOnGroupItemClick={handleOnGroupItemClick}
        setIsTransforming={setIsTransforming}
        cursorIsOnCanvasItem={cursorIsOnCanvasItem}
        removeIsOnCanvasItem={removeIsOnCanvasItem}
        canDraggable={true}
        color={settingColors.drawingObject}
        key={`draft-${draftingObject["objectId"]}`}
        object={draftingObject}
      />
    );
  }, [draftingObject, settingColors.drawingObject]);

  return (
    <>
      {isDetectedFieldsShown && drawDetectedFieldElements(detectedFieldObjects)}
      {drawProjectObjectElements}
      {drawRectangleDraftingElements}

      {!isNil(hoveringRectObject) && (
        <Label x={tooltipData.x} y={tooltipData.y} opacity={0.7}>
          <Tag
            fill={"black"}
            pointerDirection={tooltipData.pointerDirection}
            pointerWidth={10}
            pointerHeight={10}
            lineJoin={"round"}
            shadowColor={"black"}
            shadowBlur={10}
            shadowOffsetX={10}
            shadowOffsetY={10}
            shadowOpacity={0.2}
          />
          <Text
            text="double-click to show properties"
            fill={"white"}
            fontSize={12}
            padding={5}
          />
        </Label>
      )}
    </>
  );
};

export default NewItemsBoard;
